import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Citation } from '../../../libs/services/StreamingChatService';

const AGENT_DISPLAY_NAMES: Record<string, string> = {
    EmployeeAgent: 'İnsan Kaynakları ajanı',
    Employee: 'İnsan Kaynakları',
    FinanceAgent: 'Finans ajanı',
    Finance: 'Finans',
    BIAgent: 'İş Zekası ajanı',
    BI: 'İş Zekası',
    CompetitionBoardAgent: 'Rekabet Analizi ajanı',
    CompetitionBoard: 'Rekabet Analizi',
    CustomerFirstAgent: 'Müşteri Hizmetleri ajanı',
    CustomerFirst: 'Müşteri Hizmetleri',
    CurrentAccountReconciliationAgent: 'Cari Hesap ajanı',
    CurrentAccountReconciliation: 'Cari Hesap',
    DocumentAnalysisAgent: 'Belge Analizi',
    DocumentAnalysis: 'Belge Analizi',
    CBAAgent: 'ÇBA ajanı',
    CBA: 'ÇBA',
    ChatDocumentsSearchAgent: 'Belge Arama ajanı',
    ChatDocumentsSearch: 'Belge Arama',
    DocumentSearchAgent: 'Belge Arama ajanı',
    DocumentSearch: 'Belge Arama',
    InvoiceAgent: 'Fatura ajanı',
    Invoice: 'Fatura',
    ITAgent: 'BT Hizmetleri ajanı',
    IT: 'BT Hizmetleri',
    ServiceDeskAgent: 'Servis Masası ajanı',
    ServiceDesk: 'Servis Masası',
    RAGAgent: 'Bilgi Tabanı ajanı',
    RAG: 'Bilgi Tabanı',
    PersonalDevelopmentCoachAgent: 'Kişisel Gelişim ajanı',
    PersonalDevelopmentCoach: 'Kişisel Gelişim',
    WebSearchAgent: 'Web Arama ajanı',
    WebSearch: 'Web Arama',
    OnOAgent: 'ONO ajanı',
    OnO: 'ONO',
};

export const getAgentDisplayName = (name: string): string => AGENT_DISPLAY_NAMES[name] ?? name;

export interface AgentPanel {
    agentName: string;
    status: 'running' | 'completed' | 'error';
    content: string;
    startTime: number;
    endTime?: number;
    isExpanded: boolean;
    renderMode: 'raw' | 'markdown';
}

export interface StreamingState {
    isStreaming: boolean;
    superAgentBuffer: string;
    agentPanels: AgentPanel[];
    citations: Citation[];
    module: string;
    transitionPhase: 'idle' | 'agents-running' | 'transition' | 'final-response';
    // Single-line progress sentence shown next to the waiting dots until the first
    // answer token arrives. Replaced in place as stages advance; null = hidden.
    liveStatus: string | null;
    error?: string;
}

export type StreamingSliceState = Record<string, StreamingState>;

const initialState: StreamingSliceState = {};

const getOrCreateStreamState = (state: StreamingSliceState, chatId: string): StreamingState => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!state[chatId]) {
        state[chatId] = {
            isStreaming: false,
            superAgentBuffer: '',
            agentPanels: [],
            citations: [],
            module: '',
            transitionPhase: 'idle',
            liveStatus: null,
        };
    }
    return state[chatId];
};

export const streamingSlice = createSlice({
    name: 'streaming',
    initialState,
    reducers: {
        startStreaming: (state, action: PayloadAction<{ chatId: string }>) => {
            const { chatId } = action.payload;
            state[chatId] = {
                isStreaming: true,
                superAgentBuffer: '',
                agentPanels: [],
                citations: [],
                module: '',
                transitionPhase: 'idle',
                liveStatus: 'Sorunuzu aldım, üzerinde çalışıyorum...',
            };
        },
        appendSuperAgentToken: (state, action: PayloadAction<{ chatId: string; token: string }>) => {
            const { chatId, token } = action.payload;
            const streamState = getOrCreateStreamState(state, chatId);
            streamState.superAgentBuffer += token;
            streamState.transitionPhase = 'final-response';
            // First answer token arrived — the waiting status line is no longer needed.
            streamState.liveStatus = null;
            // Auto-collapse all agent panels when super-agent response starts
            for (const panel of streamState.agentPanels) {
                panel.isExpanded = false;
            }
        },
        addAgentPanel: (state, action: PayloadAction<{ chatId: string; agentName: string }>) => {
            const { chatId, agentName } = action.payload;
            const streamState = getOrCreateStreamState(state, chatId);
            streamState.transitionPhase = 'agents-running';
            const existingPanel = streamState.agentPanels.find((p) => p.agentName === agentName);
            if (!existingPanel) {
                streamState.agentPanels.push({
                    agentName,
                    status: 'running',
                    content: '',
                    startTime: Date.now(),
                    isExpanded: false,
                    renderMode: 'raw',
                });
            }
            // Update the waiting status line: name the running service(s).
            const runningCount = streamState.agentPanels.filter((p) => p.status === 'running').length;
            streamState.liveStatus =
                runningCount > 1
                    ? 'Birden fazla birime aynı anda danışıyorum...'
                    : `${getAgentDisplayName(agentName)} birimine danışıyorum...`;
        },
        appendAgentToken: (state, action: PayloadAction<{ chatId: string; agentName: string; token: string }>) => {
            const { chatId, agentName, token } = action.payload;
            const streamState = getOrCreateStreamState(state, chatId);
            const panel = streamState.agentPanels.find((p) => p.agentName === agentName);
            if (panel) {
                // First token from this panel → it has started answering.
                if (panel.content.length === 0) {
                    streamState.liveStatus = `${getAgentDisplayName(agentName)} yanıtlıyor...`;
                }
                panel.content += token;
            }
        },
        completeAgentPanel: (state, action: PayloadAction<{ chatId: string; agentName: string }>) => {
            const { chatId, agentName } = action.payload;
            const streamState = getOrCreateStreamState(state, chatId);
            const panel = streamState.agentPanels.find((p) => p.agentName === agentName);
            if (panel) {
                panel.status = 'completed';
                panel.endTime = Date.now();
                panel.renderMode = 'markdown';
                panel.isExpanded = false;
            }
            // Check if all panels are completed
            const allCompleted = streamState.agentPanels.every((p) => p.status === 'completed');
            if (allCompleted && streamState.agentPanels.length > 0) {
                streamState.transitionPhase = 'transition';
                // All services done — final answer is being assembled.
                streamState.liveStatus = 'Yanıtınız derleniyor...';
            }
        },
        setTransitionPhase: (
            state,
            action: PayloadAction<{
                chatId: string;
                phase: 'idle' | 'agents-running' | 'transition' | 'final-response';
            }>,
        ) => {
            const { chatId, phase } = action.payload;
            const streamState = getOrCreateStreamState(state, chatId);
            streamState.transitionPhase = phase;
        },
        // Replace the single-line waiting status in place (used by backend `status` events in 1b).
        setLiveStatus: (state, action: PayloadAction<{ chatId: string; label: string | null }>) => {
            const { chatId, label } = action.payload;
            const streamState = getOrCreateStreamState(state, chatId);
            streamState.liveStatus = label;
        },
        finalizeStreaming: (
            state,
            action: PayloadAction<{ chatId: string; citations: Citation[]; module: string }>,
        ) => {
            const { chatId, citations, module } = action.payload;
            const streamState = getOrCreateStreamState(state, chatId);
            streamState.isStreaming = false;
            streamState.citations = citations;
            streamState.module = module;
            streamState.transitionPhase = 'idle';
            streamState.liveStatus = null;
        },
        setStreamingError: (state, action: PayloadAction<{ chatId: string; error: string }>) => {
            const { chatId, error } = action.payload;
            const streamState = getOrCreateStreamState(state, chatId);
            streamState.isStreaming = false;
            streamState.error = error;
            streamState.transitionPhase = 'idle';
            streamState.liveStatus = null;
        },
        clearStreaming: (state, action: PayloadAction<{ chatId: string }>) => {
            const { chatId } = action.payload;
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete state[chatId];
        },
    },
});

export const {
    startStreaming,
    appendSuperAgentToken,
    addAgentPanel,
    appendAgentToken,
    completeAgentPanel,
    setTransitionPhase,
    setLiveStatus,
    finalizeStreaming,
    setStreamingError,
    clearStreaming,
} = streamingSlice.actions;

export default streamingSlice.reducer;
